import React from 'react';

// Inline SVG side-panel mockups that mirror the real Lead Finder Person/Company
// panels (Overview step). Ported from the web-install-flow reference.
// Decorative artwork only — no real data, fixed palette (matches the install
// illustrations convention in install-illustrations.tsx).

// Soft neutral bars for the placeholder text blocks.
const BAR_DARK = '#D6D2CB'; // primary text bars
const BAR_MED = '#E0DCD4'; // secondary text bars
const BAR_SOFT = '#EDE9E1'; // tertiary content blocks

// The square mock is contained (whole panel visible at reference scale); see
// __card-svg / __card-visual in _lead-finder-extension-modal.scss.
export const PersonCardMock: React.FC = () => (
  <svg
    viewBox="0 0 180 180"
    className="bs-lf-ext__card-svg"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <rect width="180" height="180" fill="#FAF8F4" />
    <rect x="10" y="6" width="160" height="168" rx="6" fill="white" stroke="#E8E4DE" />

    {/* Tab row */}
    <text x="50" y="20" fontWeight="700" fontSize="7" fill="#1976FF" textAnchor="middle">
      Person
    </text>
    <rect x="14" y="24" width="72" height="2" fill="#1976FF" />
    <text x="128" y="20" fontWeight="500" fontSize="7" fill="#9CA3AF" textAnchor="middle">
      Company
    </text>

    {/* Avatar + decision-maker dot */}
    <circle cx="30" cy="48" r="11" fill={BAR_SOFT} />
    <circle cx="37" cy="56" r="4" fill="#F59E0B" />

    {/* Name + role bars */}
    <rect x="46" y="42" width="58" height="6" rx="2" fill={BAR_DARK} />
    <rect x="46" y="52" width="76" height="3" rx="1" fill={BAR_MED} />

    {/* Decision maker pill */}
    <rect x="108" y="40" width="48" height="10" rx="5" fill="#FEF3C7" />
    <text x="132" y="47" fontWeight="700" fontSize="5" fill="#D97706" textAnchor="middle">
      ★ Decision Maker
    </text>

    {/* About paragraph */}
    <rect x="14" y="70" width="152" height="3" rx="1" fill={BAR_SOFT} />
    <rect x="14" y="77" width="130" height="3" rx="1" fill={BAR_SOFT} />
    <rect x="14" y="84" width="146" height="3" rx="1" fill={BAR_SOFT} />

    {/* CTAs */}
    <rect x="14" y="94" width="152" height="13" rx="3" fill="#1976FF" />
    <text x="90" y="103" fontWeight="700" fontSize="7" fill="white" textAnchor="middle">
      View Email · 1 cr
    </text>
    <rect x="14" y="110" width="152" height="13" rx="3" fill="white" stroke="#1976FF" />
    <text x="90" y="119" fontWeight="700" fontSize="7" fill="#1976FF" textAnchor="middle">
      View Email + Phone · 7 cr
    </text>

    {/* Details block */}
    <text x="14" y="135" fontWeight="700" fontSize="5" fill="#9CA3AF">
      PERSON&apos;S DETAILS
    </text>
    <rect x="14" y="140" width="50" height="3" rx="1" fill={BAR_MED} />
    <rect x="120" y="140" width="46" height="3" rx="1" fill={BAR_DARK} />
    <rect x="14" y="148" width="40" height="3" rx="1" fill={BAR_MED} />
    <rect x="130" y="148" width="36" height="3" rx="1" fill={BAR_DARK} />

    {/* Skills */}
    <text x="14" y="163" fontWeight="700" fontSize="5" fill="#9CA3AF">
      SKILLS
    </text>
    <rect x="14" y="167" width="28" height="7" rx="2" fill={BAR_SOFT} />
    <rect x="46" y="167" width="34" height="7" rx="2" fill={BAR_SOFT} />
    <rect x="84" y="167" width="28" height="7" rx="2" fill={BAR_SOFT} />
  </svg>
);

export const CompanyCardMock: React.FC = () => (
  <svg
    viewBox="0 0 180 180"
    className="bs-lf-ext__card-svg"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <rect width="180" height="180" fill="#FAF8F4" />
    <rect x="10" y="6" width="160" height="168" rx="6" fill="white" stroke="#E8E4DE" />

    {/* Tab row */}
    <text x="50" y="20" fontWeight="500" fontSize="7" fill="#9CA3AF" textAnchor="middle">
      Person
    </text>
    <text x="128" y="20" fontWeight="700" fontSize="7" fill="#1976FF" textAnchor="middle">
      Company
    </text>
    <rect x="94" y="24" width="72" height="2" fill="#1976FF" />

    {/* Logo + name */}
    <rect x="14" y="38" width="22" height="22" rx="4" fill={BAR_SOFT} />
    <text x="25" y="53" fontWeight="700" fontSize="11" fill="#1976FF" textAnchor="middle">
      M
    </text>
    <rect x="42" y="42" width="50" height="6" rx="2" fill={BAR_DARK} />
    <rect x="42" y="52" width="60" height="3" rx="1" fill="#1976FF" />

    {/* CTAs */}
    <rect x="14" y="72" width="152" height="13" rx="3" fill="#1976FF" />
    <text x="90" y="81" fontWeight="700" fontSize="7" fill="white" textAnchor="middle">
      Enrich Company · 0.1 cr
    </text>
    <rect x="14" y="88" width="152" height="13" rx="3" fill="white" stroke="#E8E4DE" />
    <text x="90" y="97" fontWeight="700" fontSize="7" fill={BAR_DARK} textAnchor="middle">
      View Decision Makers
    </text>

    {/* About section */}
    <text x="14" y="115" fontWeight="700" fontSize="5" fill="#9CA3AF">
      ABOUT
    </text>
    <rect x="14" y="120" width="44" height="3" rx="1" fill={BAR_MED} />
    <rect x="124" y="120" width="42" height="3" rx="1" fill={BAR_DARK} />
    <rect x="14" y="128" width="40" height="3" rx="1" fill={BAR_MED} />
    <rect x="120" y="128" width="46" height="3" rx="1" fill={BAR_DARK} />
    <rect x="14" y="136" width="36" height="3" rx="1" fill={BAR_MED} />
    <rect x="130" y="136" width="36" height="3" rx="1" fill={BAR_DARK} />

    {/* Gated sections */}
    <text x="14" y="152" fontWeight="700" fontSize="5" fill="#9CA3AF">
      PEOPLE
    </text>
    <text x="14" y="162" fontWeight="700" fontSize="5" fill="#9CA3AF">
      GROWTH
    </text>
    <text x="14" y="172" fontWeight="700" fontSize="5" fill="#9CA3AF">
      TECHNOLOGY
    </text>
  </svg>
);
